import { ImgID } from "../../../image_loader/img_id.js";
import type { OfferRewardDto, OfferRewardType } from "../../../api/dto/offers.dto.js";
import { Vector2 } from "../../../common/vector2.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { Game } from "../../../game.js";
import { KEY_HUNT_KEYS_NEEDED, KeyHuntService, type KeyHuntTask } from "../../../key_hunt/key_hunt_service.js";
import { KeyHuntAnalytics } from "../../../key_hunt/key_hunt_analytics.js";
import { mysteryBoxRewardIcon } from "../../../offers/mystery_box_offer.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AbstractDialog } from "../abstract_dialog.js";
import { DialogManager } from "../dialog_manager.js";
import { TournamentTitle } from "../tournament_title.js";
import { KeyHuntKeybarFill, type KeybarPart } from "./key_hunt_keybar_fill.js";
import { KeyHuntTaskRow } from "./key_hunt_task_row.js";
import { KeyHuntLayout, KEY_HUNT_TITLE_BOX, KEY_HUNT_TITLE_EN_FONT_SCALE, KEY_HUNT_WINDOW_SCALE } from "./key_hunt_layout.js";
import { SupportedLanguages } from "../../../localization/supported_languages.js";

/** Keeps press feedback but lets DialogManager clear its transparent canvas before the repaint. */
class DialogSafePressImage extends UIImage
{
  public override forceRenderParent(): void
  {
    this.forceMakeShouldBeRendered();
  }
}

/**
 * Which layer is on top of the event window (Figma flows 1-7):
 * main — tasks and the key bar (or "prize claimed" once the safe is open);
 * choose — "Choose a prize!", three cards; safe_closed — the full safe,
 * "tap to continue"; safe_open — the open safe with the prize and CLAIM.
 * The rules are KeyHuntInfoOverlay, above the window like the other info overlays.
 */
type KeyHuntPhase = "main" | "choose" | "safe_closed" | "safe_open";

interface Tween
{
  start: number;
  duration: number;
  apply: ( t: number ) => void;
  done?: () => void;
}

const easeOut = ( t: number ): number => 1 - ( 1 - t ) * ( 1 - t );
const easeInOut = ( t: number ): number => t < 0.5 ? 2 * t * t : 1 - Math.pow( -2 * t + 2, 2 ) / 2;

/** Overshoots to ~1.1 and settles: the pop of the safe and the cards. */
const easeOutBack = ( t: number ): number =>
{
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow( t - 1, 3 ) + c1 * Math.pow( t - 1, 2 );
};

/**
 * Key Hunt event window, laid out in the 1080x1920 Figma frame centred in the
 * content area. State comes from KeyHuntService (server state, claims applied
 * optimistically); the dialog only renders it and calls its actions. Every state change
 * rebuilds the tree on the next update, animations run in between on live elements.
 */
export class KeyHuntDialog extends AbstractDialog
{
  private phase: KeyHuntPhase = "main";
  private pending_rebuild = false;
  private busy = false;
  private tweens: Tween[] = [];

  private timer_text: UITextElement | null = null;
  private timer_value = "";
  private next_timer_update_at = 0;
  private counter_text: UITextElement | null = null;
  private keybar_fill: KeyHuntKeybarFill | null = null;
  private big_key: UIImage | null = null;
  private hint_cursor: UIImage | null = null;
  private hint_rest = new Vector2( 0, 0 );
  private hint_started_at = 0;
  private hint_was_visible = false;
  /** The closed safe waiting for the tap: it breathes and wiggles now and then. */
  private safe_idle: { safe: UIImage; since: number } | null = null;
  /** Set by the opening sequence: the open-safe layer then plays the reveal instead of a plain pop. */
  private safe_reveal = false;
  private title_language: SupportedLanguages | null = null;
  private seen_revision = KeyHuntService.revision();

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( 1080 );
    const body_h = LayoutScaler.heightFromDesign( 1920 );

    const body_x = Math.round( Game.safeAreaLeft() + ( Game.getContentWidth() - body_w ) / 2 );
    const body_y = Math.round( Game.safeAreaTop() + ( Game.getContentHeight() - body_h ) / 2 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    // Reopening with all keys collected continues the safe sequence.
    if ( KeyHuntService.isSafeReady() )
      this.phase = KeyHuntService.chosenPrize() ? "safe_closed" : "choose";

    this.createElements();
  }

  public override update( now: number ): void
  {
    super.update( now );

    // The title's font is fitted to the text at build time; a language switch only swaps the text.
    if ( this.title_language !== LocalizationManager.getCurrentLanguage() && !this.busy )
      this.pending_rebuild = true;

    if ( this.seen_revision !== KeyHuntService.revision() && !this.busy && this.phase === "main" )
    {
      this.seen_revision = KeyHuntService.revision();
      this.pending_rebuild = true;
    }

    if ( this.pending_rebuild )
    {
      this.pending_rebuild = false;
      this.createElements();
    }

    this.updateTweens( now );
    this.updateTimer( now );
    this.updateHintCursor( now );
    this.updateSafeIdle( now );
  }

  private createElements(): void
  {
    this.tweens = [];
    this.busy = false;
    this.timer_text = null;
    this.counter_text = null;
    this.keybar_fill = null;
    this.big_key = null;
    this.hint_cursor = null;
    this.safe_idle = null;
    this.removeAllChildren();

    this.createBackground();

    if ( KeyHuntService.get().prize_claimed )
    {
      this.createClaimedContent();
    }
    else
    {
      this.createKeyBar();
      this.createTasks();
      this.createTimer();
      if ( this.phase === "main" && !KeyHuntService.chosenPrize() )
        this.createHintCursor();
    }

    this.createTitle( this );
    this.createInfoButton();
    this.createCloseButton();

    if ( this.phase !== "main" )
      this.createPopup();

    this.timer_value = "";
    this.updateTimer( performance.now(), true );
    this.invalidateSubtree();
  }

  private setPhase( phase: KeyHuntPhase ): void
  {
    this.phase = phase;
    this.pending_rebuild = true;
  }

  // ---- window ----

  /**
   * The Daily Tasks capsule (corners 1:1 with the art), the event banner in its top
   * and the darker tasks panel, lined up with the key bar panel.
   */
  private createBackground(): void
  {
    const capsule = new UINineSliceElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 11 ),
      KeyHuntLayout.yFromDesign( 195 ),
      KeyHuntLayout.widthFromDesign( 1055 ),
      KeyHuntLayout.heightFromDesign( 1663 ),
      ImgID.UI_DailyTasksScreenContentBg,
      "daily_challenge_content_border",
      {
        top_left: { width: KeyHuntLayout.widthFromDesign( 128 ), height: KeyHuntLayout.heightFromDesign( 128 ) },
        bottom_right: { width: KeyHuntLayout.widthFromDesign( 124 ), height: KeyHuntLayout.heightFromDesign( 129 ) },
      }
    );
    this.addDecoration( this, capsule );

    const banner = new UIImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( 23 ),
      KeyHuntLayout.yFromDesign( 208 ),
      KeyHuntLayout.widthFromDesign( 1030 ),
      KeyHuntLayout.heightFromDesign( 713 ),
      ImgID.UI_KeyHuntBanner
    );
    this.addDecoration( this, banner );

    const panel_corner = { width: KeyHuntLayout.widthFromDesign( 98 ), height: KeyHuntLayout.heightFromDesign( 98 ) };
    const tasks_panel = new UINineSliceElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 49 ),
      KeyHuntLayout.yFromDesign( 960 ),
      KeyHuntLayout.widthFromDesign( 983 ),
      KeyHuntLayout.heightFromDesign( 747 ),
      ImgID.UI_DailyTasksTasksBg,
      "tasks_bg",
      { top_left: panel_corner, bottom_right: panel_corner }
    );
    this.addDecoration( this, tasks_panel );
  }

  /** The ribbon with "Key Hunt" lettered over it; stays lit above the popups' dim too. */
  private createTitle( holder: UIElement ): void
  {
    const ribbon = new UIImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( 21 ),
      KeyHuntLayout.yFromDesign( 85 ),
      KeyHuntLayout.widthFromDesign( 1037 ),
      KeyHuntLayout.heightFromDesign( 276.5 ),
      ImgID.UI_TournamentTopSubstrate
    );
    this.addDecoration( holder, ribbon );

    const box = KEY_HUNT_TITLE_BOX;
    this.title_language = LocalizationManager.getCurrentLanguage();
    const font_scale = this.title_language === SupportedLanguages.EN ? KEY_HUNT_TITLE_EN_FONT_SCALE : 1;
    const layers = TournamentTitle.createTitleLayers(
      this.ctx,
      KeyHuntLayout.xFromDesign( box.x ),
      KeyHuntLayout.yFromDesign( box.y ),
      {
        localization_key: "KEY_HUNT.TITLE",
        w: box.w * KEY_HUNT_WINDOW_SCALE,
        h: box.h * KEY_HUNT_WINDOW_SCALE,
        font_size: box.font_size * font_scale * KEY_HUNT_WINDOW_SCALE,
      }
    );
    for ( const layer of layers )
      this.addDecoration( holder, layer );
  }

  private createInfoButton(): void
  {
    const button = new DialogSafePressImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( 12 ),
      KeyHuntLayout.yFromDesign( 149 ),
      KeyHuntLayout.widthFromDesign( 129 ),
      KeyHuntLayout.heightFromDesign( 129 ),
      ImgID.UI_DailyTasksScreenInfo,
      {},
      true
    );
    this.setButtonAction( button, () => OverlayFactory.openOverlay( OverlayId.KEY_HUNT_INFO_OVERLAY ) );
    this.addChild( button );
  }

  private createCloseButton(): void
  {
    const button = new DialogSafePressImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( 960 ),
      KeyHuntLayout.yFromDesign( 158 ),
      KeyHuntLayout.widthFromDesign( 104 ),
      KeyHuntLayout.heightFromDesign( 104 ),
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    this.setButtonAction( button, () => this.close( "dismissed" ) );
    this.addChild( button );
  }

  // ---- main content ----

  private createKeyBar(): void
  {
    const keys = KeyHuntService.keys();

    const keybar_corner = { width: KeyHuntLayout.widthFromDesign( 112 ), height: KeyHuntLayout.heightFromDesign( 100 ) };
    const keybar_panel = new UINineSliceElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 40 ),
      KeyHuntLayout.yFromDesign( 723 ),
      KeyHuntLayout.widthFromDesign( 1000 ),
      KeyHuntLayout.heightFromDesign( 208 ),
      ImgID.UI_KeyHuntKeybarPanel,
      "key_hunt_keybar_panel",
      { top_left: keybar_corner, bottom_right: keybar_corner }
    );
    this.addDecoration( this, keybar_panel );

    const track = new UINineSliceElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 240 ),
      KeyHuntLayout.yFromDesign( 777 ),
      KeyHuntLayout.widthFromDesign( 606 ),
      KeyHuntLayout.heightFromDesign( 98 ),
      ImgID.UI_KeyHuntKeybarTrack,
      "key_hunt_keybar_track"
    );
    this.addDecoration( this, track );

    // One orange gradient over the frame's band and its inner rim, so the gaps between the
    // segments run top to bottom without the rim's dark line.
    const gaps = new RoundedRectangle(
      this.ctx,
      KeyHuntLayout.xFromDesign( 248 ),
      KeyHuntLayout.yFromDesign( 781 ),
      KeyHuntLayout.widthFromDesign( 590 ),
      KeyHuntLayout.heightFromDesign( 90 ),
      KeyHuntLayout.cornerRadiusFromDesign( 45 ),
      {
        type: "linear-gradient",
        direction: "vertical",
        stops: [
          { offset: 0, color: "#F8C50F" },
          { offset: 1, color: "#D77B22" },
        ],
      }
    );
    this.addDecoration( this, gaps );

    // Three blue pills, one per key; the middle one is wider for the counter.
    for ( const segment of KeyHuntDialog.KEYBAR_SEGMENTS )
    {
      const segment_bg = new RoundedRectangle(
        this.ctx,
        KeyHuntLayout.xFromDesign( segment.x ),
        KeyHuntLayout.yFromDesign( 793 ),
        KeyHuntLayout.widthFromDesign( segment.w ),
        KeyHuntLayout.heightFromDesign( 66 ),
        KeyHuntLayout.cornerRadiusFromDesign( 33 ),
        "#0359A4",
        undefined,
        false,
        "#04457E",
        KeyHuntLayout.widthFromDesign( 2 )
      );
      this.addDecoration( this, segment_bg );
    }

    // One fill for the whole bar, seen through the pills: its windows are the pills' own px
    // rects (rounding a separate 2 px inset left the fill 2 px short on the right, 1 px at the bottom).
    const parts = KeyHuntDialog.keybarPartsPx();
    this.keybar_fill = new KeyHuntKeybarFill(
      this.ctx,
      KeyHuntLayout.xFromDesign( KeyHuntDialog.KEYBAR_SEGMENTS[0].x ),
      KeyHuntLayout.yFromDesign( 793 ),
      parts[parts.length - 1].x + parts[parts.length - 1].w,
      KeyHuntLayout.heightFromDesign( 66 ),
      parts,
      KeyHuntDialog.keybarFillEnd( keys ),
      KeyHuntLayout.cornerRadiusFromDesign( 33 )
    );
    this.addDecoration( this, this.keybar_fill );

    this.counter_text = new UITextElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 394 ),
      KeyHuntLayout.yFromDesign( 798 ),
      KeyHuntLayout.widthFromDesign( 300 ),
      KeyHuntLayout.heightFromDesign( 56 ),
      {
        text: `${keys}/${KEY_HUNT_KEYS_NEEDED}`,
        font_size: KeyHuntLayout.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_width: KeyHuntLayout.textBorderSizeFromDesign( 8 ),
        outline_color: "rgba(3, 47, 86, 0.61)",
      }
    );
    this.addDecoration( this, this.counter_text );

    this.createPrizeSocket();

    this.big_key = new UIImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( 79 ),
      KeyHuntLayout.yFromDesign( 732 ),
      KeyHuntLayout.widthFromDesign( 177 ),
      KeyHuntLayout.heightFromDesign( 192 ),
      ImgID.UI_KeyHuntKeyBig
    );
    this.big_key.setClickable( true );
    RewardTooltip.attachName( this.big_key, () => LocalizationManager.t( "KEY_HUNT.KEY_NAME" ) );
    this.addChild( this.big_key );
  }

  /** The "+" opens the prize choice; once chosen, the socket shows the prize. Its content presses with it. */
  /** The closed safe's centre, the open safe's centre and its open door (design px, frame scale). */
  private static readonly SAFE_CLOSED_CENTER = new Vector2( 519, 827.5 );
  private static readonly SAFE_OPEN_CENTER = new Vector2( 562, 610 );
  private static readonly SAFE_DOOR = new Vector2( 557, 595 );
  /** The white flash that hides the closed-to-open swap: its size (design px) and peak opacity. */
  private static readonly SAFE_FLASH_SIZE = 800;
  private static readonly SAFE_FLASH_ALPHA = 0.85;

  /** The key bar's three blue pills in design px; the middle one is wider for the counter. */
  private static readonly KEYBAR_SEGMENTS = [ { x: 256, w: 175 }, { x: 444.5, w: 198.5 }, { x: 656.5, w: 173.5 } ];

  private static readonly PRIZE_SOCKET_CENTER = new Vector2( 934, 824 );
  private static readonly PRIZE_SOCKET_SIZE = 162;
  private static readonly PRIZE_SOCKET_SCALE = 1;

  /** The pills in px relative to the first one, computed exactly as the pills themselves are. */
  private static keybarPartsPx(): KeybarPart[]
  {
    const origin = KeyHuntLayout.xFromDesign( KeyHuntDialog.KEYBAR_SEGMENTS[0].x );
    return KeyHuntDialog.KEYBAR_SEGMENTS.map( segment => ( {
      x: KeyHuntLayout.xFromDesign( segment.x ) - origin,
      w: KeyHuntLayout.widthFromDesign( segment.w ),
    } ) );
  }

  /** Where the fill ends for `keys` keys, px from the first pill's left edge: the end of that key's pill. */
  private static keybarFillEnd( keys: number ): number
  {
    if ( keys <= 0 ) return 0;
    const part = KeyHuntDialog.keybarPartsPx()[Math.min( keys, KEY_HUNT_KEYS_NEEDED ) - 1];
    return part.x + part.w;
  }

  private createPrizeSocket(): void
  {
    const k = KeyHuntDialog.PRIZE_SOCKET_SCALE;
    const size = KeyHuntDialog.PRIZE_SOCKET_SIZE * k;
    const socket = new DialogSafePressImage(
      this.ctx,
      KeyHuntLayout.xFromDesign( KeyHuntDialog.PRIZE_SOCKET_CENTER.x - size / 2 ),
      KeyHuntLayout.yFromDesign( KeyHuntDialog.PRIZE_SOCKET_CENTER.y - size / 2 ),
      KeyHuntLayout.widthFromDesign( size ),
      KeyHuntLayout.heightFromDesign( size ),
      ImgID.UI_KeyHuntKeybarSocket,
      {},
      true
    );
    this.setButtonAction( socket, () =>
    {
      if ( !KeyHuntService.chosenPrize() )
        this.setPhase( "choose" );
    } );

    const prize = KeyHuntService.chosenPrize();
    if ( prize )
    {
      const icon = new UIImage(
        this.ctx,
        KeyHuntLayout.widthFromDesign( 18.5 * k ),
        KeyHuntLayout.heightFromDesign( -3 * k ),
        KeyHuntLayout.widthFromDesign( 133 * k ),
        KeyHuntLayout.heightFromDesign( 133 * k ),
        KeyHuntDialog.getRewardImgId( prize.type )
      );
      icon.setClickable( false );
      socket.addChild( icon, true );

      const amount = new UITextElement(
        this.ctx,
        KeyHuntLayout.widthFromDesign( 21 * k ),
        KeyHuntLayout.heightFromDesign( 101 * k ),
        KeyHuntLayout.widthFromDesign( 122 * k ),
        KeyHuntLayout.heightFromDesign( 56 * k ),
        {
          text: KeyHuntDialog.getRewardAmountText( prize ),
          font_size: KeyHuntLayout.textSizeFromDesign( 42 * k ),
          min_font_size: KeyHuntLayout.textSizeFromDesign( 26 * k ),
          font_weight: 900,
          text_style: TextStyleScheme.geologica,
          text_color: ColorScheme.white,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          auto_wrap: false,
          scale_to_width: true,
          outline_width: KeyHuntLayout.textBorderSizeFromDesign( 9 * k ),
          outline_color: "#003F87",
        }
      );
      amount.setClickable( false );
      socket.addChild( amount, true );

      RewardTooltip.attach( socket, prize.type );
    }
    else
    {
      const plus = new UIImage(
        this.ctx,
        KeyHuntLayout.widthFromDesign( 38 * k ),
        KeyHuntLayout.heightFromDesign( 40 * k ),
        KeyHuntLayout.widthFromDesign( 86 * k ),
        KeyHuntLayout.heightFromDesign( 87 * k ),
        ImgID.UI_KeyHuntPlus
      );
      plus.setClickable( false );
      socket.addChild( plus, true );
    }

    this.addChild( socket );
  }

  /** Rows (visible 5..940 of the 945px bitmap) sit 12px inside the panel's fill. */
  private createTasks(): void
  {
    const row_y = [ 976, 1156, 1335, 1515 ];

    KeyHuntService.orderedTasks().forEach( ( task, index ) =>
    {
      const row = new KeyHuntTaskRow(
        this.ctx,
        KeyHuntLayout.xFromDesign( 68 ),
        KeyHuntLayout.yFromDesign( row_y[index] ),
        task,
        claimed => this.claimTask( claimed, row )
      );
      this.addChild( row );
    } );
  }

  /** Dark pill with a light lip at the bottom edge. */
  private createTimer(): void
  {
    const timer_bg = new RoundedRectangle(
      this.ctx,
      KeyHuntLayout.xFromDesign( 201 ),
      KeyHuntLayout.yFromDesign( 1729 ),
      KeyHuntLayout.widthFromDesign( 679 ),
      KeyHuntLayout.heightFromDesign( 80 ),
      KeyHuntLayout.cornerRadiusFromDesign( 40 ),
      "#0D62B2",
      undefined,
      false,
      "#2B4B89",
      KeyHuntLayout.widthFromDesign( 2 ),
      { color: "rgba(255, 255, 255, 0.32)", blur: 0, offsetX: 0, offsetY: KeyHuntLayout.heightFromDesign( 4 ) }
    );
    this.addDecoration( this, timer_bg );

    this.timer_text = this.createTimerText( 204, 1729, 673, 80, 40 );
    this.addDecoration( this, this.timer_text );
  }

  private createTimerText( x: number, y: number, w: number, h: number, font_size: number ): UITextElement
  {
    return new UITextElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( x ),
      KeyHuntLayout.yFromDesign( y ),
      KeyHuntLayout.widthFromDesign( w ),
      KeyHuntLayout.heightFromDesign( h ),
      {
        text: "",
        font_size: KeyHuntLayout.textSizeFromDesign( font_size ),
        min_font_size: KeyHuntLayout.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: KeyHuntLayout.textBorderSizeFromDesign( font_size / 5 ),
        outline_color: "#002B9A",
      }
    );
  }

  /** Flow 1: the cursor sits with its fingertip on the "+" (same anchor as FeatureTutorialOverlay). */
  private createHintCursor(): void
  {
    const cursor_w = KeyHuntLayout.widthFromDesign( 110 );
    this.hint_rest = new Vector2( KeyHuntLayout.xFromDesign( 926 ), KeyHuntLayout.yFromDesign( 803 ) );

    this.hint_cursor = new UIImage(
      this.ctx,
      this.hint_rest.x,
      this.hint_rest.y,
      cursor_w,
      cursor_w * 183 / 159,
      ImgID.IconTutorialCursor
    );
    this.hint_cursor.setOpacityImmediate( 0 );
    this.addDecoration( this, this.hint_cursor );

    // The 4 s cycle starts 3.4 s in, so the first reminder comes 0.6 s after the window opens.
    this.hint_started_at = performance.now() - 3400;
    this.hint_was_visible = false;
  }

  /**
   * The "prize claimed" ribbon at full scale over the shrunk window, wide enough for the curled
   * ends to sit past the yellow rim, but never wider than the screen.
   */
  private createClaimedRibbon(): UIImage
  {
    const banner_w = Math.min( 1055 * KEY_HUNT_WINDOW_SCALE + 100, LayoutScaler.getVisibleDesignWidth() );
    return new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( 1080 - banner_w ) / 2 ),
      LayoutScaler.yFromDesign( 706 + KeyHuntDialog.claimedRibbonDy() ),
      LayoutScaler.widthFromDesign( banner_w ),
      LayoutScaler.heightFromDesign( 254 ),
      ImgID.UI_TournamentResultBanner
    );
  }

  /** Keeps the ribbon on its Figma spot of the shrunk window while it stays at full scale. */
  private static claimedRibbonDy(): number
  {
    return ( 833 - 960 ) * KEY_HUNT_WINDOW_SCALE + 960 - 833;
  }

  /** Flow 7: the prize is taken, the window waits for the next day. */
  private createClaimedContent(): void
  {
    this.addDecoration( this, this.createClaimedRibbon() );
    const banner_dy = KeyHuntDialog.claimedRibbonDy();

    const claimed_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 120 ),
      LayoutScaler.yFromDesign( 762 + banner_dy ),
      LayoutScaler.widthFromDesign( 840 ),
      LayoutScaler.heightFromDesign( 76 ),
      {
        localization_key: "KEY_HUNT.PRIZE_CLAIMED",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 36 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: "#002B9A",
      }
    );
    this.addDecoration( this, claimed_text );

    const next_prize_text = new UITextElement(
      this.ctx,
      KeyHuntLayout.xFromDesign( 90 ),
      KeyHuntLayout.yFromDesign( 1234 ),
      KeyHuntLayout.widthFromDesign( 900 ),
      KeyHuntLayout.heightFromDesign( 60 ),
      {
        localization_key: "KEY_HUNT.NEXT_PRIZE_IN",
        font_size: KeyHuntLayout.textSizeFromDesign( 50 ),
        min_font_size: KeyHuntLayout.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: KeyHuntLayout.textBorderSizeFromDesign( 10 ),
        outline_color: "#002B9A",
      }
    );
    this.addDecoration( this, next_prize_text );

    this.timer_text = this.createTimerText( 90, 1286, 900, 60, 50 );
    this.addDecoration( this, this.timer_text );
  }

  // ---- popups ----

  private createPopup(): void
  {
    const popup = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );

    // The dim covers the whole screen, safe areas and the space around the frame included.
    const dialog_pos = this.getAbsolutePosition();
    const dim = new RoundedRectangle(
      this.ctx,
      -dialog_pos.x,
      -dialog_pos.y,
      DialogManager.getWidth(),
      DialogManager.getHeight(),
      0,
      ColorScheme.black_transparent_80
    );
    popup.addChild( dim );
    this.addChild( popup );

    switch ( this.phase )
    {
      case "choose":
        this.createChoosePopup( popup );
        break;
      case "safe_closed":
        this.createSafeClosedPopup( popup, dim );
        break;
      case "safe_open":
        this.createSafeOpenPopup( popup );
        break;
    }
  }

  /** Flow 2: three prize cards; there is no way out of this layer but choosing. */
  private createChoosePopup( popup: UIElement ): void
  {
    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 40 ),
      LayoutScaler.yFromDesign( 378 ),
      LayoutScaler.widthFromDesign( 1000 ),
      LayoutScaler.heightFromDesign( 126 ),
      {
        localization_key: "KEY_HUNT.CHOOSE_TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 110 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 66 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFCE6" },
            { offset: 0.55, color: "#FFE680" },
            { offset: 1, color: "#FFC21E" },
          ],
        },
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 22 ),
        outline_color: "#E95600",
        shadow_color: "#4A0224",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 8 ),
      }
    );
    this.addDecoration( popup, title );

    const subtitle = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 40 ),
      LayoutScaler.yFromDesign( 516 ),
      LayoutScaler.widthFromDesign( 1000 ),
      LayoutScaler.heightFromDesign( 64 ),
      {
        localization_key: "KEY_HUNT.CHOOSE_TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 800,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        shadow_color: ColorScheme.black_transparent,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    this.addDecoration( popup, subtitle );

    const card_x = [ 45, 380, 712 ];

    // Figma's soft column glow under the cards.
    for ( const x of card_x )
    {
      const glow = new RoundedRectangle(
        this.ctx,
        LayoutScaler.xFromDesign( x + 8 ),
        LayoutScaler.yFromDesign( 1414 ),
        LayoutScaler.widthFromDesign( 305 ),
        LayoutScaler.heightFromDesign( 354 ),
        { tl: LayoutScaler.cornerRadiusFromDesign( 40 ), tr: LayoutScaler.cornerRadiusFromDesign( 40 ) },
        {
          type: "linear-gradient",
          direction: "vertical",
          stops: [
            { offset: 0, color: "rgba(70, 228, 19, 0.26)" },
            { offset: 1, color: "rgba(42, 191, 21, 0)" },
          ],
        }
      );
      this.addDecoration( popup, glow );
    }

    KeyHuntService.get().prize_options.slice( 0, 3 ).forEach( ( prize, index ) =>
    {
      const card = this.createPrizeCard( card_x[index], prize, index );
      popup.addChild( card );

      card.setOpacityImmediate( 0 );
      this.animate( 320, index * 70, t =>
      {
        const scale = 0.6 + 0.4 * easeOutBack( t );
        card.setScaleImmediate( new Vector2( scale, scale ) );
        card.setOpacityImmediate( Math.min( 1, t * 2 ) );
      } );
    } );
  }

  /** The holder covers the card and its button only: a holder takes every touch inside its rect. */
  private createPrizeCard( card_x: number, prize: OfferRewardDto, index: number ): UIElementHolder
  {
    const card = new UIElementHolder(
      this.ctx,
      LayoutScaler.xFromDesign( card_x ),
      LayoutScaler.yFromDesign( 666 ),
      LayoutScaler.widthFromDesign( 315 ),
      LayoutScaler.heightFromDesign( 710 )
    );

    const card_bg = new UIImage(
      this.ctx,
      LayoutScaler.widthFromDesign( -2 ),
      LayoutScaler.heightFromDesign( -2 ),
      LayoutScaler.widthFromDesign( 319 ),
      LayoutScaler.heightFromDesign( 539 ),
      ImgID.UI_KeyHuntPrizeCard
    );
    this.addDecoration( card, card_bg );

    const icon = new UIImage(
      this.ctx,
      LayoutScaler.widthFromDesign( 52.5 ),
      LayoutScaler.heightFromDesign( 149 ),
      LayoutScaler.widthFromDesign( 210 ),
      LayoutScaler.heightFromDesign( 210 ),
      KeyHuntDialog.getRewardImgId( prize.type )
    );
    this.addDecoration( card, icon );

    const amount = new UITextElement(
      this.ctx,
      LayoutScaler.widthFromDesign( 47.5 ),
      LayoutScaler.heightFromDesign( 306 ),
      LayoutScaler.widthFromDesign( 220 ),
      LayoutScaler.heightFromDesign( 76 ),
      {
        text: KeyHuntDialog.getRewardAmountText( prize ),
        font_size: LayoutScaler.textSizeFromDesign( 69 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 41 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
        outline_color: "#003F87",
      }
    );
    this.addDecoration( card, amount );

    const button_w = LayoutScaler.widthFromDesign( 307 );
    const choose_button = new DialogSafePressImage(
      this.ctx,
      LayoutScaler.widthFromDesign( 6 ),
      LayoutScaler.heightFromDesign( 559 ),
      button_w,
      LayoutScaler.heightFromDesign( 151 ),
      ImgID.UI_KeyHuntChooseBtn,
      {},
      true
    );
    this.setButtonAction( choose_button, () => this.choosePrize( index ) );

    const choose_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.heightFromDesign( 47 ),
      button_w,
      LayoutScaler.heightFromDesign( 58 ),
      {
        localization_key: "CHOOSE_PROGRESS_DIALOG.SELECT",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 800,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    choose_button.addChild( choose_text, true );
    card.addChild( choose_button );

    return card;
  }

  /** Flow 5: the safe with all keys; a tap anywhere opens it (openSafe). */
  private createSafeClosedPopup( popup: UIElement, dim: UIElement ): void
  {
    this.createTitle( popup );

    const safe = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 358 ),
      LayoutScaler.yFromDesign( 667 ),
      LayoutScaler.widthFromDesign( 322 ),
      LayoutScaler.heightFromDesign( 321 ),
      ImgID.UI_KeyHuntSafeClosed
    );
    safe.enableRotation();
    this.addDecoration( popup, safe );
    this.popIn( safe, 420 );
    this.safe_idle = { safe, since: performance.now() + 500 };

    const hint = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 60 ),
      LayoutScaler.yFromDesign( 1702 ),
      LayoutScaler.widthFromDesign( 960 ),
      LayoutScaler.heightFromDesign( 62 ),
      {
        localization_key: "RESULT.CONTINUE",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        shadow_color: ColorScheme.black_transparent,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    hint.setOpacityImmediate( 0 );
    this.addDecoration( popup, hint );
    this.animate( 300, 450, t => hint.setOpacityImmediate( t ) );

    this.setButtonAction( dim, () => this.openSafe( popup, safe, hint ) );
  }

  /**
   * The safe rises to where the open one stands and trembles harder and harder, squashes,
   * and a white flash covers the swap to the open-safe layer, which plays the reveal.
   */
  private openSafe( popup: UIElement, safe: UIImage, hint: UIElement ): void
  {
    if ( this.busy ) return;
    this.busy = true;
    this.safe_idle = null;
    void audioController.playSound( AudioClips.METAL_CUBE_HIT );

    const from = safe.pixelPosImportant().clone();
    const lift = LayoutScaler.heightFromDesign( KeyHuntDialog.SAFE_CLOSED_CENTER.y - KeyHuntDialog.SAFE_OPEN_CENTER.y );
    const shift = LayoutScaler.widthFromDesign( KeyHuntDialog.SAFE_OPEN_CENTER.x - KeyHuntDialog.SAFE_CLOSED_CENTER.x );
    const hint_opacity = hint.getOpacityImportant();

    this.animate( 200, 0, t => hint.setOpacityImmediate( hint_opacity * ( 1 - t ) ) );

    // Rise and tremble: the swing grows from 2 to 8 degrees, the safe swells a little.
    this.animate( 700, 0, t =>
    {
      const eased = easeInOut( t );
      safe.setPixelPos( new Vector2( from.x + shift * eased, from.y - lift * eased ), true );
      safe.setRotationImmediate( Math.sin( t * Math.PI * 14 ) * ( 2 + 6 * t ) * Math.PI / 180 );
      const swell = 1 + 0.08 * eased;
      safe.setScaleImmediate( new Vector2( swell, swell ) );
    }, () =>
    {
      safe.setRotationImmediate( 0 );

      // Squash before the burst.
      this.animate( 140, 0, t =>
      {
        const k = Math.sin( Math.PI * t );
        safe.setScaleImmediate( new Vector2( 1.08 + 0.12 * k, 1.08 - 0.14 * k ) );
      } );

      const flash = this.createGlowImage( popup, ImgID.CircleGlow1, KeyHuntDialog.SAFE_OPEN_CENTER, KeyHuntDialog.SAFE_FLASH_SIZE );
      flash.setScaleImmediate( new Vector2( 0.1, 0.1 ) );
      flash.setOpacityImmediate( 0 );
      this.animate( 220, 80, t =>
      {
        const scale = 0.1 + 0.9 * easeOut( t );
        flash.setScaleImmediate( new Vector2( scale, scale ) );
        flash.setOpacityImmediate( KeyHuntDialog.SAFE_FLASH_ALPHA * t );
      }, () =>
      {
        void audioController.playSound( AudioClips.BOOSTER_APPEAR );
        this.safe_reveal = true;
        this.setPhase( "safe_open" );
      } );
    } );
  }

  /** Between the pop and the tap: a slow breath and a short wiggle every 2.2 s. */
  private updateSafeIdle( now: number ): void
  {
    const idle = this.safe_idle;
    if ( !idle || this.busy || now < idle.since ) return;

    const elapsed = now - idle.since;
    const breath = 1 + 0.025 * Math.sin( elapsed / 1000 * Math.PI * 1.2 );
    idle.safe.setScaleImmediate( new Vector2( breath, breath ) );

    const cycle = ( elapsed % 2200 ) / 450;
    const wiggle = cycle < 1 ? Math.sin( cycle * Math.PI * 4 ) * ( 1 - cycle ) * 5 * Math.PI / 180 : 0;
    idle.safe.setRotationImmediate( wiggle );
  }

  /** A square glow image centred on `center` (design px). */
  private createGlowImage( holder: UIElement, img_id: ImgID, center: Vector2, size_design: number ): UIImage
  {
    const size = LayoutScaler.widthFromDesign( size_design );
    const glow = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( center.x ) - size / 2,
      LayoutScaler.yFromDesign( center.y ) - size / 2,
      size,
      size,
      img_id
    );
    this.addDecoration( holder, glow );
    return glow;
  }

  /**
   * Flow 6: the open safe gives out the chosen prize. After openSafe the flash fades off the
   * open safe, sparks burst from its door and the prize card flies out of it; CLAIM comes last.
   */
  private createSafeOpenPopup( popup: UIElement ): void
  {
    const reveal = this.safe_reveal;
    this.safe_reveal = false;

    this.createTitle( popup );

    const safe = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 372 ),
      LayoutScaler.yFromDesign( 395 ),
      LayoutScaler.widthFromDesign( 421 ),
      LayoutScaler.heightFromDesign( 421 ),
      ImgID.UI_KeyHuntSafeOpen
    );
    this.addDecoration( popup, safe );

    if ( reveal )
    {
      this.busy = true;
      this.animate( 420, 0, t =>
      {
        const scale = 0.8 + 0.2 * easeOutBack( t );
        safe.setScaleImmediate( new Vector2( scale, scale ) );
      } );
      this.createSparks( popup );

      const flash = this.createGlowImage( popup, ImgID.CircleGlow1, KeyHuntDialog.SAFE_OPEN_CENTER, KeyHuntDialog.SAFE_FLASH_SIZE );
      this.animate( 480, 0, t =>
      {
        const scale = 1 + 0.3 * easeOut( t );
        flash.setScaleImmediate( new Vector2( scale, scale ) );
        flash.setOpacityImmediate( KeyHuntDialog.SAFE_FLASH_ALPHA * ( 1 - t ) );
      }, () => popup.removeChild( flash ) );
    }
    else
    {
      this.popIn( safe, 380 );
    }

    const prize = KeyHuntService.chosenPrize();
    if ( prize )
    {
      const reward = this.createRewardCard( prize );
      popup.addChild( reward );
      if ( reveal )
      {
        this.flyRewardOut( reward );
      }
      else
      {
        reward.setOpacityImmediate( 0 );
        this.animate( 260, 260, t => reward.setOpacityImmediate( t ) );
      }
    }

    const claim_w = LayoutScaler.widthFromDesign( 598 );
    const claim_button = new DialogSafePressImage(
      this.ctx,
      LayoutScaler.xFromDesign( 252 ),
      LayoutScaler.yFromDesign( 1408 ),
      claim_w,
      LayoutScaler.heightFromDesign( 274 ),
      ImgID.UI_GreenFrameBtn,
      {},
      true
    );
    this.setButtonAction( claim_button, () => this.claimPrize() );

    const claim_text = new UITextElement(
      this.ctx,
      0,
      0,
      claim_w,
      LayoutScaler.heightFromDesign( 260 ),
      {
        localization_key: "CLAIM",
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 800,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    claim_button.addChild( claim_text, true );
    popup.addChild( claim_button );

    if ( reveal )
    {
      claim_button.setScaleImmediate( new Vector2( 0, 0 ) );
      this.animate( 380, 900, t =>
      {
        const scale = Math.max( 0, easeOutBack( t ) );
        claim_button.setScaleImmediate( new Vector2( scale, scale ) );
      }, () => { this.busy = false; } );
    }
  }

  /** The prize card leaves the safe's open door small, arcs up and lands at full size. */
  private flyRewardOut( reward: UIElementHolder ): void
  {
    const to = reward.pixelPosImportant().clone();
    const w = reward.getRect().getW();
    const h = reward.getRect().getH();
    const from = new Vector2(
      LayoutScaler.xFromDesign( KeyHuntDialog.SAFE_DOOR.x ) - w / 2,
      LayoutScaler.yFromDesign( KeyHuntDialog.SAFE_DOOR.y ) - h / 2
    );
    const arc = LayoutScaler.heightFromDesign( 220 );

    reward.setOpacityImmediate( 0 );
    reward.setScaleImmediate( new Vector2( 0.2, 0.2 ) );
    reward.setPixelPos( from, true );

    this.animate( 560, 220, t =>
    {
      const eased = easeOut( t );
      reward.setPixelPos( new Vector2(
        from.x + ( to.x - from.x ) * eased,
        from.y + ( to.y - from.y ) * eased - Math.sin( Math.PI * t ) * arc
      ), true );
      const scale = 0.2 + 0.8 * Math.max( 0, easeOutBack( t ) );
      reward.setScaleImmediate( new Vector2( scale, scale ) );
      reward.setOpacityImmediate( Math.min( 1, t * 4 ) );
    } );
  }

  /** Gold sparks fly out of the open door and fade. */
  private createSparks( popup: UIElement ): void
  {
    const count = 8;
    for ( let i = 0; i < count; i++ )
    {
      const angle = -Math.PI / 2 + ( i / count - 0.5 ) * Math.PI * 1.6 + ( Math.random() - 0.5 ) * 0.3;
      const distance = LayoutScaler.widthFromDesign( 180 + Math.random() * 140 );
      const size_design = 50 + Math.random() * 40;
      const spark = this.createGlowImage( popup, i % 2 ? ImgID.SparkGlow2 : ImgID.CircleGlow3, KeyHuntDialog.SAFE_DOOR, size_design );
      const start = spark.pixelPosImportant().clone();
      const fall = LayoutScaler.heightFromDesign( 120 );
      spark.setOpacityImmediate( 0 );

      this.animate( 520 + Math.random() * 260, 40 + Math.random() * 120, t =>
      {
        const eased = easeOut( t );
        spark.setPixelPos( new Vector2(
          start.x + Math.cos( angle ) * distance * eased,
          start.y + Math.sin( angle ) * distance * eased + fall * t * t
        ), true );
        const scale = 1 - 0.6 * t;
        spark.setScaleImmediate( new Vector2( scale, scale ) );
        spark.setOpacityImmediate( t < 0.15 ? t / 0.15 : 1 - ( t - 0.15 ) / 0.85 );
      }, () => popup.removeChild( spark ) );
    }
  }

  /** The reward card of GetRewardOverlay and the level result (RewardFrame's native 228x282). */
  private createRewardCard( prize: OfferRewardDto ): UIElementHolder
  {
    const reward = new UIElementHolder(
      this.ctx,
      LayoutScaler.xFromDesign( 437 ),
      LayoutScaler.yFromDesign( 862 ),
      LayoutScaler.widthFromDesign( 228 ),
      LayoutScaler.heightFromDesign( 282 )
    );
    reward.setClickable( false );

    const card_bg = new UIImage(
      this.ctx,
      0,
      0,
      LayoutScaler.widthFromDesign( 228 ),
      LayoutScaler.heightFromDesign( 282 ),
      ImgID.UI_CoinBg
    );
    this.addDecoration( reward, card_bg );

    const icon = new UIImage(
      this.ctx,
      LayoutScaler.widthFromDesign( 27 ),
      LayoutScaler.heightFromDesign( 20 ),
      LayoutScaler.widthFromDesign( 174 ),
      LayoutScaler.heightFromDesign( 174 ),
      KeyHuntDialog.getRewardImgId( prize.type )
    );
    this.addDecoration( reward, icon );

    const amount = new UITextElement(
      this.ctx,
      LayoutScaler.widthFromDesign( 10 ),
      LayoutScaler.heightFromDesign( 200 ),
      LayoutScaler.widthFromDesign( 208 ),
      LayoutScaler.heightFromDesign( 58 ),
      {
        text: KeyHuntDialog.getRewardAmountText( prize ),
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.addDecoration( reward, amount );

    return reward;
  }

  // ---- actions ----

  private choosePrize( index: number ): void
  {
    if ( !KeyHuntService.choosePrize( index ) ) return;
    this.setPhase( KeyHuntService.isSafeReady() ? "safe_closed" : "main" );
  }

  /** Flow 4: the row's key flies into the big key, then the bar fills one segment. */
  private claimTask( task: KeyHuntTask, row: KeyHuntTaskRow ): void
  {
    const big_key = this.big_key;
    if ( !big_key || this.busy || this.phase !== "main" ) return;
    if ( !KeyHuntService.claimTask( task.id ) ) return;

    void audioController.playSound( AudioClips.UI_CLICK );
    this.busy = true;

    const row_key = row.getKeyImage();
    const key_w = row_key.getRect().getW();
    const key_h = row_key.getRect().getH();
    const row_pos = row.pixelPosImportant();
    const row_key_pos = row_key.pixelPosImportant();
    const big_key_pos = big_key.pixelPosImportant();

    const from = new Vector2( row_pos.x + row_key_pos.x, row_pos.y + row_key_pos.y );
    const to = new Vector2(
      big_key_pos.x + ( big_key.getRect().getW() - key_w ) / 2,
      big_key_pos.y + ( big_key.getRect().getH() - key_h ) / 2
    );
    const scale_to = big_key.getRect().getH() / key_h;
    const arc_h = KeyHuntLayout.heightFromDesign( 140 );

    const flying_key = new UIImage(
      this.ctx,
      from.x,
      from.y,
      key_w,
      key_h,
      ImgID.UI_KeyHuntKeyBig
    );
    this.addDecoration( this, flying_key );

    this.animate( 560, 0, t =>
    {
      const eased = easeOut( t );
      const arc = Math.sin( Math.PI * t ) * arc_h;
      const scale = 1 + ( scale_to - 1 ) * eased;

      flying_key.setPixelPos( new Vector2(
        from.x + ( to.x - from.x ) * eased,
        from.y + ( to.y - from.y ) * eased - arc
      ), true );
      flying_key.setScaleImmediate( new Vector2( scale, scale ) );
      flying_key.setOpacityImmediate( t < 0.85 ? 1 : ( 1 - t ) / 0.15 );
    }, () =>
    {
      this.removeChild( flying_key );
      this.growKeyBar( KeyHuntService.keys() );
    } );
  }

  /** The fill runs on from the previous key's pill into the new one's while the big key pulses. */
  private growKeyBar( to_keys: number ): void
  {
    this.counter_text?.setText( `${to_keys}/${KEY_HUNT_KEYS_NEEDED}` );

    const fill = this.keybar_fill;
    const end = KeyHuntDialog.keybarFillEnd( to_keys );
    const start = to_keys > 1 ? KeyHuntDialog.keybarFillEnd( to_keys - 1 ) : KeyHuntLayout.widthFromDesign( 64 );

    const big_key = this.big_key;
    this.animate( 420, 0, t =>
    {
      const pulse = 1 + 0.18 * Math.sin( Math.PI * t );
      fill?.setFillEnd( start + ( end - start ) * easeOut( t ) );
      big_key?.setScaleImmediate( new Vector2( pulse, pulse ) );
    }, () =>
    {
      if ( KeyHuntService.isSafeReady() )
        this.setPhase( KeyHuntService.chosenPrize() ? "safe_closed" : "choose" );
      else
        this.pending_rebuild = true;
    } );
  }

  private claimPrize(): void
  {
    if ( !KeyHuntService.claimPrize() ) return;
    this.setPhase( "main" );
  }

  // ---- per-frame updates ----

  private updateTimer( now: number, force = false ): void
  {
    if ( !this.timer_text ) return;
    if ( !force && now < this.next_timer_update_at ) return;
    this.next_timer_update_at = now + 1000;

    const time_left = formatShortDuration( KeyHuntService.timeLeftMs() );
    const value = KeyHuntService.get().prize_claimed
      ? time_left
      : LocalizationManager.tWithParams( "KEY_HUNT.TIME_LEFT", [ time_left ] );
    if ( value === this.timer_value ) return;

    this.timer_value = value;
    this.timer_text.setText( value );
    this.invalidateSubtree();
  }

  /** Until a prize is picked, the cursor fades in on the "+" every 4 s, presses twice and fades out. */
  private updateHintCursor( now: number ): void
  {
    const cursor = this.hint_cursor;
    if ( !cursor ) return;

    const period_ms = 4000;
    const visible_ms = 1700;
    const fade_ms = 200;
    const press_scale = 0.8;

    const t = ( now - this.hint_started_at ) % period_ms;
    const visible = t < visible_ms;
    if ( !visible && !this.hint_was_visible ) return;
    this.hint_was_visible = visible;

    let opacity = 1;
    if ( !visible ) opacity = 0;
    else if ( t < fade_ms ) opacity = t / fade_ms;
    else if ( t > visible_ms - fade_ms ) opacity = ( visible_ms - t ) / fade_ms;

    // Two presses inside the visible window.
    const press_t = Math.max( 0, Math.min( 1, ( t - fade_ms ) / ( visible_ms - 2 * fade_ms ) ) );
    const press = visible ? Math.max( 0, Math.sin( press_t * Math.PI * 4 ) ) : 0;
    const scale = 1 - ( 1 - press_scale ) * press;

    // Scale is centred, so the top-left moves with it to keep the fingertip on the button.
    const shift = 1 - scale;
    cursor.setPixelPos( new Vector2(
      this.hint_rest.x - shift * cursor.getRect().getW2(),
      this.hint_rest.y - shift * cursor.getRect().getH2()
    ), true );
    cursor.setScaleImmediate( new Vector2( scale, scale ) );
    cursor.setOpacityImmediate( opacity );
    this.invalidateSubtree();
  }

  private updateTweens( now: number ): void
  {
    if ( !this.tweens.length ) return;

    for ( const tween of [ ...this.tweens ] )
    {
      const t = Math.min( 1, Math.max( 0, ( now - tween.start ) / tween.duration ) );
      tween.apply( t );
      if ( t >= 1 )
      {
        this.tweens.splice( this.tweens.indexOf( tween ), 1 );
        tween.done?.();
      }
    }
    this.invalidateSubtree();
  }

  // ---- helpers ----

  private animate( duration: number, delay: number, apply: ( t: number ) => void, done?: () => void ): void
  {
    this.tweens.push( { start: performance.now() + delay, duration, apply, done } );
  }

  private popIn( element: UIElement, duration: number ): void
  {
    element.setScaleImmediate( new Vector2( 0, 0 ) );
    this.animate( duration, 0, t =>
    {
      const scale = Math.max( 0, easeOutBack( t ) );
      element.setScaleImmediate( new Vector2( scale, scale ) );
    } );
  }

  /** Buttons click with a sound and stay inert while a claim animation runs. */
  private setButtonAction( button: UIElement, action: () => void ): void
  {
    button.setOnClick( () =>
    {
      if ( this.busy ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      action();
    } );
  }

  /** Art and labels never take touches; only buttons and tooltip targets do. */
  private addDecoration( holder: UIElement, element: UIElement ): void
  {
    element.setClickable( false );
    holder.addChild( element );
  }

  /** Figma draws the coin pile and the 1+1 heart here; the rest as in the Mystery Box. */
  private static getRewardImgId( type: OfferRewardType ): ImgID
  {
    if ( type === "coins" ) return ImgID.UI_OnePlusOneOfferCoins;
    if ( type === "infinite_hearts_minutes" ) return ImgID.UI_OnePlusOneOfferInfiniteHeart;
    return mysteryBoxRewardIcon( type );
  }

  /** "x3", "300", "1h". */
  private static getRewardAmountText( reward: OfferRewardDto ): string
  {
    if ( reward.type === "coins" ) return String( reward.amount );
    if ( reward.type === "infinite_hearts_minutes" || reward.type === "no_ads_minutes" )
    {
      const hours = reward.amount / 60;
      return hours % 1 === 0 ? `${hours}h` : `${reward.amount}m`;
    }
    return `x${reward.amount}`;
  }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public static open(): KeyHuntDialog
  {
    KeyHuntAnalytics.opened( KeyHuntService.getPayload()?.key_hunt );
    void KeyHuntService.fetch();
    return DialogManager.openPooled( "KeyHuntDialog", undefined, () => new KeyHuntDialog() ) as KeyHuntDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse(): void
  {
    this.seen_revision = KeyHuntService.revision();
    this.phase = "main";
    if ( KeyHuntService.isSafeReady() )
      this.phase = KeyHuntService.chosenPrize() ? "safe_closed" : "choose";
    this.safe_reveal = false;
    this.pending_rebuild = false;
    this.rebuildChildren( () => this.createElements() );
  }

  public name(): string
  {
    return "KeyHuntDialog";
  }
}
